import * as React from 'react'

import { Box, Form, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceReviewProcessVisibilitySettingsFragment,
  UpdatePerformanceReviewVisibilitySettingDocument,
} from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import {
  SettingsVisibility,
  type TypeSettingsVisibility,
} from 'modules/performance_process/components/SettingsVisibility'

type FormType = TypeSettingsVisibility & {
  id: string
}

export const Visibility: React.FC<{
  review: PerformanceReviewProcessVisibilitySettingsFragment
}> = ({ review }) => {
  const [updateReviewVisibilitySetting, { loading }] = useMutation(
    UpdatePerformanceReviewVisibilitySettingDocument,
    {
      extractorFn: (data) => data.performanceMutations.updatePerformanceReviewVisibilitySetting,
    }
  )
  const reviewIsFinished = review.status == 'finished'

  const form = useForm<FormType>({
    defaultValues: {
      id: String(review.id),
      earlyAccessToAnswersForManagers:
        review.visibilitySetting?.earlyAccessToAnswersForManagers ?? true,
      restrictAnswersVisibilityToReportees:
        review.visibilitySetting?.restrictAnswersVisibilityToReportees ?? false,
      anonymousPeerEvaluationForTarget:
        review.visibilitySetting?.anonymousPeerEvaluationForTarget ?? false,
    },
    validate: (data) => {
      const errors: FormErrors<FormType> = {}
      if (!data.id) {
        errors.id = i18n.t('common.empty')
      }

      return errors
    },
    onSubmit: async (data) => {
      const result = await updateReviewVisibilitySetting({
        variables: {
          id: data.id,
          earlyAccessToAnswersForManagers: data.earlyAccessToAnswersForManagers,
          restrictAnswersVisibilityToReportees: data.restrictAnswersVisibilityToReportees,
          anonymousPeerEvaluationForTarget: data.anonymousPeerEvaluationForTarget,
        },
      })

      return handleResult(result, () => undefined)
    },
  })

  const onChange = (field: keyof TypeSettingsVisibility, enabled: boolean) => {
    form.setValues({
      ...form.values,
      [field]: enabled,
    })
    form.handleSubmit()
  }

  const disabled = form.isSubmitting || loading || !form.isValid || reviewIsFinished

  return (
    <Box gap="s32">
      <Box gap="s16">
        <Text size="400" color="grey1000" weight="semibold">
          {i18n.t('performance_process.reviews.visibility')}
        </Text>
        <Text size="300">
          {i18n.t('performance_process.reviews.visibility_section.description')}
        </Text>
      </Box>
      {review.visibilitySetting?.migratedAnonymous && (
        <Callout
          title={i18n.t('performance_process.reviews.visibility_section.migrated_anonymous.title')}
          type="info"
        >
          <Box gap="s4">
            <Text size="300">
              {i18n.t(
                'performance_process.reviews.visibility_section.migrated_anonymous.description'
              )}
            </Text>
            <Text size="300">
              {i18n.t(
                'performance_process.reviews.visibility_section.migrated_anonymous.description_2'
              )}
            </Text>
          </Box>
        </Callout>
      )}
      <Form onSubmit={() => form.handleSubmit()} padding={null}>
        <SettingsVisibility
          disabled={disabled}
          settings={{
            earlyAccessToAnswersForManagers: form.values.earlyAccessToAnswersForManagers,
            restrictAnswersVisibilityToReportees: form.values.restrictAnswersVisibilityToReportees,
            anonymousPeerEvaluationForTarget: form.values.anonymousPeerEvaluationForTarget,
          }}
          onSettingToggled={onChange}
        />
      </Form>
    </Box>
  )
}
